/**
 * Prompt Registry Index
 *
 * Central registry of all LLM prompts used by agents.
 * Part of Phase 3: LLM Prompt Centralization.
 *
 * This file re-exports all prompts from domain modules and provides
 * registry functions for prompt lookup and management.
 *
 * @module config/prompts
 */
// ═══════════════════════════════════════════════════════════════════════════
// RE-EXPORT ALL DOMAIN PROMPTS
// ═══════════════════════════════════════════════════════════════════════════
export * from './agent-loop-prompts.js';
export * from './builder-prompts.js';
export * from './curator-prompts.js';
export * from './harvester-prompts.js';
export * from './reviewer-prompts.js';
export * from './vimalakirti-prompts.js';
export * from './transformation-prompts.js';
export * from './sic-prompts.js';
export * from './model-master-prompts.js';
export * from './prospector-prompts.js';
export * from './refiner-prompts.js';
export * from './archivist-prompts.js';
export * from './summarize-cull-prompts.js';
// ═══════════════════════════════════════════════════════════════════════════
// IMPORT GROUPED ARRAYS
// ═══════════════════════════════════════════════════════════════════════════
import { AGENT_LOOP_PROMPTS } from './agent-loop-prompts.js';
import { BUILDER_PROMPTS, BUILDER_INLINE_OUTLINE, BUILDER_INLINE_COMPOSE, BUILDER_INLINE_STRUCTURE_ANALYSIS, BUILDER_INLINE_REVISION, BUILDER_INLINE_SUGGESTIONS, } from './builder-prompts.js';
import { CURATOR_PROMPTS, CURATOR_INLINE_PASSAGE_ASSESSMENT, CURATOR_INLINE_THREAD_COHERENCE, CURATOR_INLINE_CLUSTER_SUGGESTION, } from './curator-prompts.js';
import { HARVESTER_PROMPTS } from './harvester-prompts.js';
import { REVIEWER_PROMPTS } from './reviewer-prompts.js';
import { VIMALAKIRTI_PROMPTS } from './vimalakirti-prompts.js';
import { TRANSFORMATION_PROMPTS } from './transformation-prompts.js';
import { SIC_PROMPTS } from './sic-prompts.js';
import { MODEL_MASTER_PROMPTS } from './model-master-prompts.js';
import { PROSPECTOR_PROMPTS, PROSPECTOR_INLINE_RAW_GEM } from './prospector-prompts.js';
import { REFINER_PROMPTS, REFINER_INLINE_VERIFY, REFINER_INLINE_QUICK_VERIFY } from './refiner-prompts.js';
import { ARCHIVIST_PROMPTS } from './archivist-prompts.js';
import { SUMMARIZE_CULL_PROMPTS } from './summarize-cull-prompts.js';
// ═══════════════════════════════════════════════════════════════════════════
// COMBINED REGISTRY
// ═══════════════════════════════════════════════════════════════════════════
/**
 * All prompts combined into a single array.
 */
export const ALL_PROMPTS = [
    ...AGENT_LOOP_PROMPTS,
    ...BUILDER_PROMPTS,
    ...CURATOR_PROMPTS,
    ...HARVESTER_PROMPTS,
    ...REVIEWER_PROMPTS,
    ...VIMALAKIRTI_PROMPTS,
    ...TRANSFORMATION_PROMPTS,
    ...SIC_PROMPTS,
    ...MODEL_MASTER_PROMPTS,
    ...PROSPECTOR_PROMPTS,
    ...REFINER_PROMPTS,
    ...ARCHIVIST_PROMPTS,
    ...SUMMARIZE_CULL_PROMPTS,
];
/**
 * Inline migration prompts (formerly hardcoded in houses).
 * Kept as separate array for backwards compatibility.
 */
export const INLINE_MIGRATION_PROMPTS = [
    CURATOR_INLINE_PASSAGE_ASSESSMENT,
    CURATOR_INLINE_THREAD_COHERENCE,
    CURATOR_INLINE_CLUSTER_SUGGESTION,
    BUILDER_INLINE_OUTLINE,
    BUILDER_INLINE_COMPOSE,
    BUILDER_INLINE_STRUCTURE_ANALYSIS,
    BUILDER_INLINE_REVISION,
    BUILDER_INLINE_SUGGESTIONS,
    PROSPECTOR_INLINE_RAW_GEM,
    REFINER_INLINE_VERIFY,
    REFINER_INLINE_QUICK_VERIFY,
];
/**
 * Prompt registry mapping ID to definition.
 */
export const PROMPT_REGISTRY = Object.fromEntries(ALL_PROMPTS.map((p) => [p.id, p]));
/**
 * Category mapping for all prompts.
 */
export const PROMPT_CATEGORIES = {
    // Agent Loop
    AGENT_LOOP_SYSTEM: 'agent-loop',
    AGENT_LOOP_REASONING: 'agent-loop',
    // Builder
    BUILDER_OUTLINE_CREATION: 'builder',
    BUILDER_SECTION_COMPOSITION: 'builder',
    BUILDER_TRANSITION_GENERATION: 'builder',
    BUILDER_STRUCTURE_ANALYSIS: 'builder',
    BUILDER_DRAFT_REVISION: 'builder',
    BUILDER_IMPROVEMENT_SUGGESTIONS: 'builder',
    BUILDER_STYLE_ANALYSIS: 'builder',
    BUILDER_ISSUE_FIX: 'builder',
    BUILDER_FOCUSED_REVISION: 'builder',
    BUILDER_INLINE_OUTLINE: 'builder',
    BUILDER_INLINE_COMPOSE: 'builder',
    BUILDER_INLINE_STRUCTURE_ANALYSIS: 'builder',
    BUILDER_INLINE_REVISION: 'builder',
    BUILDER_INLINE_SUGGESTIONS: 'builder',
    // Curator
    CURATOR_PASSAGE_ASSESSMENT: 'curator',
    CURATOR_THREAD_COHERENCE: 'curator',
    CURATOR_CLUSTER_SUGGESTION: 'curator',
    CURATOR_CARD_ASSIGNMENT: 'curator',
    CURATOR_INLINE_PASSAGE_ASSESSMENT: 'curator',
    CURATOR_INLINE_THREAD_COHERENCE: 'curator',
    CURATOR_INLINE_CLUSTER_SUGGESTION: 'curator',
    // Harvester
    HARVESTER_PASSAGE_EXTRACTION: 'harvester',
    HARVESTER_THEME_IDENTIFICATION: 'harvester',
    HARVEST_DRAFT_GENERATION: 'harvester',
    // Reviewer
    REVIEWER_STYLE_CHECK: 'reviewer',
    REVIEWER_STRUCTURE_CHECK: 'reviewer',
    REVIEWER_FACT_EXTRACTION: 'reviewer',
    // Vimalakirti
    VIMALAKIRTI_INQUIRY_LEVEL: 'vimalakirti',
    VIMALAKIRTI_PROFESSIONAL_DISTANCE: 'vimalakirti',
    VIMALAKIRTI_SHADOW_CHECK: 'vimalakirti',
    // Transformation
    TRANSFORMATION_SYSTEM: 'transform',
    TRANSFORMATION_PERSONA: 'transform',
    TRANSFORMATION_STYLE: 'transform',
    TRANSFORMATION_NAMESPACE_EXTRACT: 'transform',
    TRANSFORMATION_NAMESPACE_MAP: 'transform',
    TRANSFORMATION_NAMESPACE_RECONSTRUCT: 'transform',
    // SIC
    SIC_GENRE_DETECTION: 'reviewer',
    SIC_EXTRACTOR: 'reviewer',
    SIC_JUDGE: 'reviewer',
    SIC_STYLE_CHECK_EXTRACTOR: 'reviewer',
    SIC_STYLE_CHECK_JUDGE: 'reviewer',
    SIC_PROFILE_VETTING: 'reviewer',
    // Model Master (Utility)
    MODEL_MASTER_TRANSLATE: 'utility',
    MODEL_MASTER_ANALYZE: 'utility',
    MODEL_MASTER_SUMMARIZE: 'utility',
    MODEL_MASTER_DETECT_AI: 'utility',
    MODEL_MASTER_HUMANIZE: 'utility',
    // Prospector (Content Excellence System)
    PROSPECTOR_EXCELLENCE_SCORE: 'prospector',
    PROSPECTOR_RAW_GEM_DETECTION: 'prospector',
    PROSPECTOR_INLINE_RAW_GEM: 'prospector',
    // Refiner (Content Excellence System)
    REFINER_EXTRACT_INSIGHT: 'refiner',
    REFINER_VERIFY_PRESERVATION: 'refiner',
    REFINER_POLISH_EXPRESSION: 'refiner',
    REFINER_INLINE_VERIFY: 'refiner',
    REFINER_INLINE_QUICK_VERIFY: 'refiner',
    // Archivist (Content Excellence System)
    ARCHIVIST_CATEGORIZE_EXPRESSION: 'archivist',
    ARCHIVIST_FIND_CANONICAL: 'archivist',
    // Summarize/Cull
    SUMMARIZE_BRIEF: 'transform',
    SUMMARIZE_DETAILED: 'transform',
    HARVEST_CULL_RELEVANCE: 'utility',
};
// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Get a prompt by ID.
 */
export function getPrompt(id) {
    return PROMPT_REGISTRY[id];
}
/**
 * Get all prompts for an agent.
 */
export function getPromptsForAgent(agentName) {
    return ALL_PROMPTS.filter((p) => p.usedBy?.includes(agentName));
}
/**
 * Get all prompts in a category.
 */
export function getPromptsInCategory(category) {
    return ALL_PROMPTS.filter((p) => PROMPT_CATEGORIES[p.id] === category);
}
/**
 * Get all prompts requiring a capability.
 */
export function getPromptsRequiringCapability(capability) {
    return ALL_PROMPTS.filter((p) => p.requirements.capabilities.includes(capability));
}
/**
 * List all prompt IDs.
 */
export function listPromptIds() {
    return Object.keys(PROMPT_REGISTRY);
}
/**
 * Check if a prompt exists.
 */
export function hasPrompt(id) {
    return id in PROMPT_REGISTRY;
}
//# sourceMappingURL=index.js.map